import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { httpsCallable } from 'firebase/functions';

import { functionsUsCentral1 } from '@lib/firebase';
import type {
  SquarePaymentRequest,
  SquarePaymentResponse,
  SquareRefundRequest,
  SquareRefundResponse,
  SquareLocation,
} from '@lib/integrations/square';

/**
 * Hook to process Square payment
 * @deprecated Use useSquarePayment instead
 */
export function useSquare() {
  return {
    processPayment: useSquarePayment().mutateAsync,
  };
}

/**
 * Hook to process Square payment
 */
export function useSquarePayment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (paymentRequest: SquarePaymentRequest): Promise<SquarePaymentResponse> => {
      const processPayment = httpsCallable<SquarePaymentRequest, SquarePaymentResponse>(
        functionsUsCentral1,
        'processSquarePayment'
      );
      const result = await processPayment(paymentRequest);
      return result.data;
    },
    onSuccess: () => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ['square-payments'] });
      queryClient.invalidateQueries({ queryKey: ['orders'] });
    },
  });
}

/**
 * Hook to process Square refund
 */
export function useSquareRefund() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (refundRequest: SquareRefundRequest): Promise<SquareRefundResponse> => {
      const processRefund = httpsCallable<SquareRefundRequest, SquareRefundResponse>(
        functionsUsCentral1,
        'processSquareRefund'
      );
      const result = await processRefund(refundRequest);
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['square-refunds'] });
      queryClient.invalidateQueries({ queryKey: ['square-payments'] });
    },
  });
}

/**
 * Hook to get Square locations
 */
export function useSquareLocations() {
  return useQuery({
    queryKey: ['square-locations'],
    queryFn: async (): Promise<SquareLocation[]> => {
      const getLocations = httpsCallable<unknown, { locations: SquareLocation[] }>(
        functionsUsCentral1,
        'getSquareLocations'
      );
      const result = await getLocations();
      return result.data.locations;
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
}

